import type { TraderProfile as MesaTraderProfile } from '@/types/api/legacy-trader-profile'

import React, { useMemo } from 'react'

import Icon, { IconName } from '@/components/atomic/icon'
import Tooltip from '@/components/atomic/tooltip'
import { cn } from '@/utils/shadcn/utils'

import AskActionColumn from './ask-action-column'
import BidActionColumn from './bid-action-column'
import {
  type EnrichedOrderBookRowItem,
  QuoteSide,
  RFQQuoteStatus,
} from './types'

interface ActionColumnProps {
  orderBookRow: EnrichedOrderBookRowItem
  isDisabled: boolean
  borderStatus: boolean
  isHighlight: boolean
  rowHeight: number
  isButtonDisabled: boolean
  loadingState: { isLoading: boolean; index?: number }
  showActionButtonsOnAllRows: boolean
  isFirstValid: boolean
  renderOnLeft?: boolean
  // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
  CellComponent: React.ComponentType<any>
  onAction: (
    idx: number,
    orderBookRow: EnrichedOrderBookRowItem,
  ) => (traderProfile: MesaTraderProfile) => void
  idx: number
  quoteSide: QuoteSide
}

const ActionColumn = React.memo<ActionColumnProps>(
  ({
    orderBookRow,
    isDisabled,
    borderStatus,
    isHighlight,
    rowHeight,
    isButtonDisabled,
    loadingState,
    showActionButtonsOnAllRows,
    isFirstValid,
    renderOnLeft = false,
    CellComponent,
    onAction,
    idx,
    quoteSide,
  }) => {
    const rfqQuoteStatus =
      quoteSide === 'bid'
        ? orderBookRow.bidRfqQuoteStatus
        : orderBookRow.askRfqQuoteStatus

    const iconInfo = useMemo(() => {
      if (rfqQuoteStatus === RFQQuoteStatus.Expired) {
        return {
          icon: IconName.ClockIcon,
          tooltip: 'This quote was part of an RFQ that is no longer active',
        }
      }
      if (rfqQuoteStatus === RFQQuoteStatus.Unsuccessful) {
        return {
          icon: IconName.XCircleIcon,
          tooltip: `Attempted to ${quoteSide === QuoteSide.Bid ? 'hit' : 'lift'} this quote, but rejected by counterparty`,
        }
      }
      return null
    }, [rfqQuoteStatus, quoteSide])

    const isButtonLoading = loadingState.isLoading && loadingState.index === idx

    // Show icon if we have one (takes precedence over action buttons)
    if (iconInfo) {
      return (
        <CellComponent
          hasBorder={(!isHighlight || isDisabled) && borderStatus}
          className="p-0"
          height={rowHeight}
        >
          <div
            className={cn(
              'flex items-center h-8 w-[36px] m-auto',
              renderOnLeft ? 'justify-start ml-2' : 'justify-end mr-2',
            )}
          >
            <Tooltip simpleText={iconInfo.tooltip}>
              {/* This is needed because the icon is an SVG that does not support pointer-events */}
              <div className="pointer-events-auto">
                <Icon
                  iconName={iconInfo.icon}
                  size="medium"
                  className="text-moment-red-600 dark:text-moment-red-300"
                />
              </div>
            </Tooltip>
          </div>
        </CellComponent>
      )
    }

    // Show action button for enabled rows, or disabled icon for disabled rows
    if (quoteSide === 'bid') {
      return (
        <BidActionColumn
          isDisabled={isDisabled}
          borderStatus={borderStatus}
          isHighlight={isHighlight}
          rowHeight={rowHeight}
          onHitBid={onAction(idx, orderBookRow)}
          isButtonDisabled={isButtonDisabled}
          isLoading={isButtonLoading}
          showActionButtonsOnAllRows={showActionButtonsOnAllRows}
          isFirstValidBid={isFirstValid}
          renderOnLeft={renderOnLeft}
          CellComponent={CellComponent}
        />
      )
    } else {
      return (
        <AskActionColumn
          isDisabled={isDisabled}
          borderStatus={borderStatus}
          isHighlight={isHighlight}
          rowHeight={rowHeight}
          onLiftAsk={onAction(idx, orderBookRow)}
          isButtonDisabled={isButtonDisabled}
          isLoading={isButtonLoading}
          showActionButtonsOnAllRows={showActionButtonsOnAllRows}
          isFirstValidAsk={isFirstValid}
          CellComponent={CellComponent}
        />
      )
    }
  },
)
ActionColumn.displayName = 'ActionColumn'

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default ActionColumn
